import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Clock, AlertCircle } from 'lucide-react';

export default function Login() {
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(token);
      navigate('/dashboard');
    } catch (err) {
      setError('Invalid token or insufficient permissions. Admin access required.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-slate-800 rounded-lg shadow-2xl p-8 border border-slate-700">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <Clock className="w-12 h-12 text-blue-500" />
            <div>
              <h1 className="text-2xl font-bold text-white">CronMaster</h1>
              <p className="text-sm text-slate-400">Lair404 Cronjob System</p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-900/50 border border-red-700 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-200">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="token" className="block text-sm font-medium text-slate-300 mb-2">
                JWT Token
              </label>
              <textarea
                id="token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste your JWT token from login.weretrade.com"
                className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={4}
                required
              />
              <p className="mt-2 text-xs text-slate-400">
                Get your token from <a href="https://login.weretrade.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">login.weretrade.com</a>
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading || !token.trim()}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-800"
            >
              {isLoading ? 'Verifying...' : 'Login'}
            </button>
          </form>

          {/* Info */}
          <div className="mt-8 p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
            <p className="text-xs text-blue-200">
              <strong>Admin Access Required:</strong> Only users with admin role can access the cronjob management system.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
